/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Flex, Form, Modal, Splitter } from 'antd';
import { RedoOutlined } from '@ant-design/icons';
import GoogleMap from 'routes/app/zone/components/map/GoogleMap';
import { useUpdateGroupingZone, useZones, Zone } from 'services/ZoneService';
import Card from 'components/card';
import { DebouncedInputField, DebouncedTextAreaField } from 'components/Forms';
import useUserCityPosition from 'hooks/useUserCity';
import { ChildrenZonesSelector } from './components/childrenZonesSelector';

interface GroupingZoneEditParams {
  id?: string;
}

const GroupingZoneEdit = () => {
  const { id } = useParams<GroupingZoneEditParams>();
  const history = useHistory();
  const { lat, lng } = useUserCityPosition();
  const {
    zones: { data: zones },
    groupingZones: {
      isSuccess: isGroupingZonesSuccess,
      isLoading: isGroupingZonesLoading,
      isFetching: isGroupingZonesFetching,
      isPlaceholderData: isGroupingZonesPlaceholderData,
    },
    getZoneById,
  } = useZones();
  const { mutate: updateGroupingZone } = useUpdateGroupingZone();

  const [name, setName] = useState<string>();
  const [detail, setDetail] = useState<string>();
  const [open, setOpen] = useState(false);
  const [childrenZonesIds, setChildrenZonesIds] = useState<
    (number | undefined)[]
  >([]);

  const zone: Zone = useMemo(() => {
    if (
      !isGroupingZonesLoading &&
      !isGroupingZonesFetching &&
      !isGroupingZonesPlaceholderData &&
      id &&
      !Number.isNaN(Number(id))
    )
      return getZoneById(parseInt(id, 10));
    return { name: '', detail: '' };
  }, [
    getZoneById,
    isGroupingZonesLoading,
    isGroupingZonesFetching,
    isGroupingZonesPlaceholderData,
    id,
  ]);

  const selectedChildrenZones = useMemo(
    () => zones.filter((z: Zone) => z.id && childrenZonesIds.includes(z.id)),
    [childrenZonesIds, zones],
  );

  const initialChildrenZones = useMemo(() => zone.childrenZones ?? [], [zone]);

  useEffect(() => {
    if (zone) {
      setName(zone.name);
      setDetail(zone.detail);
      if (zone.childrenZones)
        setChildrenZonesIds(
          zone.childrenZones.map((z: Zone | number | undefined) => {
            if (typeof z === 'number') return z;
            if (z && typeof z === 'object' && 'id' in z) return z.id;
            return undefined;
          }),
        );
    }
  }, [zone]);

  if (!isGroupingZonesSuccess || !id) return history.push('/zones');

  const handleSubmit = () => {
    const zoneToUpdate = {
      ...zone,
      detail,
      name,
      childrenZones: childrenZonesIds,
    };

    updateGroupingZone(zoneToUpdate);
    history.goBack();
  };

  return (
    <>
      <Modal
        title={`¿Confirmar edición de zona agrupadora ${name}?`}
        okText="Confirmar"
        cancelText="Volver"
        onOk={handleSubmit}
        onCancel={() => setOpen(false)}
        open={open}
      />
      <Splitter style={{ height: '83vh' }}>
        <Splitter.Panel
          style={{
            scrollbarGutter: 'stable',
            scrollbarColor: 'lightgrey transparent',
            overflowX: 'hidden',
          }}
        >
          <Flex
            vertical
            style={{
              padding: '0 4px 0 0',
              height: '100%',
            }}
          >
            <Card title="Datos de la zona">
              <Flex justify="space-between">
                <label>Nombre:</label>
                {name !== zone.name && (
                  <RedoOutlined onClick={() => setName(zone.name)} />
                )}
              </Flex>
              <Form.Item
                validateStatus={name !== zone.name ? 'warning' : 'success'}
              >
                <DebouncedInputField
                  placeholder="Nombre"
                  value={name}
                  setValue={setName}
                />
              </Form.Item>
              <Flex justify="space-between">
                <label>Descripción:</label>
                {detail !== zone.detail && (
                  <RedoOutlined onClick={() => setDetail(zone.detail)} />
                )}
              </Flex>
              <Form.Item
                validateStatus={detail !== zone.detail ? 'warning' : 'success'}
              >
                <DebouncedTextAreaField
                  rows={6}
                  placeholder="Información adicional de esa zona"
                  value={detail}
                  setValue={setDetail}
                />
              </Form.Item>
            </Card>
            <ChildrenZonesSelector
              parentId={zone.id}
              initialChildrenZones={initialChildrenZones}
              selectedChildrenZones={selectedChildrenZones}
              setChildrenZonesIds={setChildrenZonesIds}
            />
          </Flex>
        </Splitter.Panel>
        <Splitter.Panel defaultSize="70%" min="50%" max="80%">
          <div style={{ width: '100%', height: '100%', padding: '0 0 0 14px' }}>
            <GoogleMap
              arePolygonsLoading={
                isGroupingZonesLoading ||
                isGroupingZonesFetching ||
                isGroupingZonesPlaceholderData
              }
              initialCenter={{
                lat: lat ?? -34.601692,
                lng: lng ?? -58.431461,
              }}
              polygons={selectedChildrenZones}
            />
          </div>
        </Splitter.Panel>
      </Splitter>
      <Button
        type="primary"
        onClick={() => setOpen(true)}
        disabled={!name || !detail || childrenZonesIds.length < 1}
        size="large"
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          marginRight: 80,
          marginBottom: 40,
        }}
      >
        Guardar Cambios
      </Button>
    </>
  );
};

export default GroupingZoneEdit;
